/**
 * Sidebar — RW-1071: Dual-rail themeable navigation shell.
 *
 * A slim always-present **section rail** (64px) drives a **contextual
 * sub-rail** (210px) that is hidden by default and reveals on demand:
 *   - click a section icon → its sub-rail reveals
 *   - click the same icon again, or the sub-rail ✕ → it collapses
 *   - click a different section → the sub-rail swaps
 * An accent dot on the section rail marks the group containing the current
 * page while its sub-rail is closed. Rail color is themeable (Noir / Cream /
 * Coral); in production the theme is a fixed choice (default Cream), settable
 * via the `navRailTheme` setting — NOT an in-app toggle.
 *
 * The production nav model (SECTIONS + FOOTER_ITEMS) is preserved exactly —
 * no destinations added or removed. Mobile keeps its own bottom-bar
 * (MobileNav) plus a full grouped drawer here.
 *
 * Origin: claude.ai/design "Rêve OS · Navigation Directions" → NavDualSmart.
 * Supersedes the RW-191 accordion rail.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { isFeatureEnabled } from '../lib/featureFlags';
// RW-899 stage 1: winding-down agents keep only the Deals module + account
// basics — the allowlist is shared with the App.js route gate.
import { isWindDownAllowedPath } from '../lib/windDown';
// Phase 5 (RW-1068): office-staff self-service role — surfaces "My Space".
import { isStaffSelf } from '../lib/permissions';
// RW-1302: live FLOW streak for the sidebar badge — the server-maintained
// flowLeaderboard summary updates on every scorecard write, so the badge
// moves after a submit without a reload.
import { subscribe } from '../services/data';
// RW-68: in-house Lucide set — nav glyphs are SVG, not emoji.
// RW-2378: the icon bridge is shared with MobileNav so both resolve the
// same names; it used to be private to this file (FeedIcons is imported
// there now, not here).
import NavIcon from './NavIcon';
// RW-1789: nav telemetry within the RW-22 taxonomy — rail selections, plus
// the one-shot source marker that lets App.js tag route arrivals as
// rail-reached vs otherwise.
import { track, EVENTS } from '../analytics/track';
import { markNavSource } from '../analytics/navSource';
// RW-1784: the nav tree lives in ONE config module (structure only); this
// component owns rendering, sub-rail state, flag filtering and role gating.
// RW-1785: banded sections — this rail renders the flattened rows
// (sectionItems); band labels are drawn by the two-tier chrome (RW-1786).
// RW-1787: two named trees — agent, and the staff SUPERSET (Queues/Roster/
// Admin band). Variant resolution lives here, on the predicates this file
// already has; the config stays structure-only.
import { SECTIONS, STAFF_SECTIONS, FOOTER_ITEMS, STAFF_FOOTER_ITEMS, navRoleAllows, pathActive, sectionForPath, visibleNavSections } from '../lib/navigation';
import useUserPreference, { legacyBoolean } from '../hooks/useUserPreference';
import { ModalOpenMarker } from '../hooks/useModalOpen';
import { usablePhotoURL } from '../lib/avatarPhoto';

/* ── Design tokens — inlined to avoid TDZ with theme.js at module level.
   RP.* warm palette from the source design system. ───────────────────── */
const RP = {
  coral: '#EE8172', coralDark: '#D4685A', coralInk: '#A63F33', coralSoft: '#FBEAE4',
  noir: '#0F1011', ink: '#1A1A1A',
  textSoft: '#5A5550', muted: '#8A8178', mute2: '#A8A096',
  line: '#E8E2DA', panel: '#FAF7F2', panelDeep: '#F5EFE5',
  yellow: '#EFDD84', purple: '#5C50A1',
};
const SERIF = "'Fraunces', Georgia, serif";
const SANS = "'Jost', sans-serif";

/* Rail widths (px) */
const SLIM_W = 64;
const SUB_W = 210;
/* RW-1818: the collapsed icon-only sub-rail */
const MINI_W = 46;
/* Width broadcast so App.js can offset header + main content */
const RESIZE_EVENT = 'navrailresize';
/* RW-3798: the rails start BELOW the fixed banner stack (event alert,
   impersonation, SW update, notification permission). App.js publishes that
   stack's height as --reve-shell-top on .reve-app-root, which these fixed
   columns inherit; at top: 0 the full-width banners (z 1100 / 9999) covered
   the sub-rail's header and its × Collapse button. 0px when no banner shows. */
// RW-3797: and each is border-box, so `width` is the whole box the page
// reserves for it (the navrailresize broadcast) — padding and border inside.
export function railFrame(left, width) {
  return {
    position: 'fixed', top: 'var(--reve-shell-top, 0px)', left,
    height: 'calc(100vh - var(--reve-shell-top, 0px))', width, zIndex: 60,
    boxSizing: 'border-box',
  };
}

/* Respect prefers-reduced-motion */
const REDUCED_MOTION = (() => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
})();

/* ════════════════════════════════════════════════════════════════════════
   Color themes. Each matches its single-rail variant shell: the slim rail is
   the darker surface, the sub-rail a lighter companion. Default ships Cream.
   ════════════════════════════════════════════════════════════════════════ */
const DUAL_THEMES = {
  // Noir — near-black slim rail, subtly-lifted dark sub-rail, coral accents.
  noir: {
    rail1Bg: RP.noir, rail1Border: 'none',
    tileBg: RP.coral, tileText: '#fff',
    icon: 'rgba(255,255,255,0.62)', iconActive: '#fff',
    activeBg: 'rgba(238,129,114,0.20)', accent: RP.coral,
    label: 'rgba(255,255,255,0.5)', labelActive: '#fff',
    divider: 'rgba(255,255,255,0.10)',
    rail2Bg: '#1B1C1F', rail2Border: 'rgba(255,255,255,0.08)',
    subEyebrow: RP.coral, subTitle: '#fff',
    itemText: 'rgba(255,255,255,0.72)', itemActiveText: '#fff',
    itemActiveBg: 'rgba(255,255,255,0.07)', itemActiveShadow: 'none',
    soft: 'rgba(255,255,255,0.34)', chipBorder: 'rgba(255,255,255,0.14)',
    tagBg: 'rgba(238,129,114,0.14)', tagText: RP.coral,
    closeColor: 'rgba(255,255,255,0.5)',
    badgeBg: RP.coral, badgeText: '#fff',
    avatarBorder: 'rgba(255,255,255,0.18)', avatarFallbackBg: 'rgba(255,255,255,0.12)', avatarFallbackText: '#fff',
  },
  // Cream — panelDeep slim rail, lighter panel sub-rail, white active pill.
  cream: {
    rail1Bg: RP.panelDeep, rail1Border: `1px solid ${RP.line}`,
    tileBg: RP.coral, tileText: '#fff',
    icon: RP.muted, iconActive: RP.coral,
    activeBg: '#fff', accent: RP.coral,
    label: RP.mute2, labelActive: RP.ink,
    divider: RP.line,
    rail2Bg: RP.panel, rail2Border: RP.line,
    subEyebrow: RP.coralDark, subTitle: RP.ink,
    itemText: RP.textSoft, itemActiveText: RP.ink,
    itemActiveBg: '#fff', itemActiveShadow: '0 1px 2px rgba(40,30,20,0.06)',
    soft: RP.mute2, chipBorder: RP.line,
    tagBg: RP.coralSoft, tagText: RP.coralDark,
    closeColor: RP.muted,
    badgeBg: RP.coral, badgeText: '#fff',
    avatarBorder: RP.line, avatarFallbackBg: '#E5E7EB', avatarFallbackText: '#374151',
  },
  // Coral — dark-coral slim rail, sub-rail a true ~30%-lighter coral, light text.
  coral: {
    rail1Bg: RP.coralDark, rail1Border: 'none',
    tileBg: '#fff', tileText: RP.coralDark,
    icon: 'rgba(255,255,255,0.72)', iconActive: '#fff',
    activeBg: 'rgba(255,255,255,0.18)', accent: '#fff',
    label: 'rgba(255,255,255,0.6)', labelActive: '#fff',
    divider: 'rgba(255,255,255,0.20)',
    rail2Bg: '#E1958C', rail2Border: 'rgba(255,255,255,0.28)',
    subEyebrow: 'rgba(255,255,255,0.85)', subTitle: '#fff',
    itemText: 'rgba(255,255,255,0.88)', itemActiveText: RP.coralDark,
    itemActiveBg: '#fff', itemActiveShadow: '0 1px 3px rgba(120,50,40,0.18)',
    soft: 'rgba(255,255,255,0.55)', chipBorder: 'rgba(255,255,255,0.32)',
    tagBg: '#fff', tagText: RP.coralDark,
    closeColor: 'rgba(255,255,255,0.85)',
    badgeBg: '#fff', badgeText: RP.coralDark,
    avatarBorder: 'rgba(255,255,255,0.4)', avatarFallbackBg: 'rgba(255,255,255,0.22)', avatarFallbackText: '#fff',
  },
};

function resolveTheme() {
  try {
    const t = localStorage.getItem('navRailTheme');
    if (t && DUAL_THEMES[t]) return t;
  } catch {} // eslint-disable-line no-empty
  return 'cream'; // production default (confirmed lean)
}

/* Theme options for the Settings → Appearance picker (swatch = slim-rail surface). */
export const NAV_THEME_OPTIONS = [
  { key: 'cream', label: 'Cream', swatch: RP.panelDeep },
  { key: 'noir', label: 'Noir', swatch: RP.noir },
  { key: 'coral', label: 'Coral', swatch: RP.coralDark },
];
export const NAV_THEME_EVENT = 'navrailthemechange';
export function getNavTheme() { return resolveTheme(); }

/* Nav model — extracted to src/lib/navigation.js (RW-1784, one source of
   truth). Section.icon is the slim-rail glyph; section.subtitle is the
   sub-rail serif title. */

/* ── Helpers ──
   RW-2329: pathActive and sectionForPath moved to src/lib/navigation.js (the
   pure path→nav helpers already live there) so they can be tested directly.

   sectionForPath's RULE changed with the move, and this is the only place a
   reader of this file will see it: of every row active for the pathname, the
   LONGEST wins; equal-length ties keep list order. pathActive is unchanged.

   It used to return the first section holding any active row, which made the
   answer depend on list ORDER — and STAFF_SECTIONS reaches `deals` three
   sections before `connect` (home, queues, deals, …), while `/deals`
   prefixes /deals/coming-soon. So the staff rail lit up Deals on both Market
   boards; the agent rail, which reaches `connect` first, was already right.

   NOT "exact match first, then prefix". That rule was written, tried, and
   abandoned: a record path like /deals/coming-soon/listing-42 has no row of
   its own, so an exact pass misses it entirely and the order-dependent prefix
   fallback still answered `deals` on the staff rail. Longest-match subsumes
   the exact case (an exact row IS the longest possible match) and is the only
   version that also fixes the records. See navigation.js for the full note. ── */

/* ═══════════════════════════════════════════════════════════════════════ */

export default function Sidebar({
  user, isAdmin, isTrainer = false, isStaff = false, windingDown = false, mobile, badges = {}, onLogout, onboardingProgress = null,
  onboardingComplete = false, onboardingNewCount = 0,
  // RW-1642: item keys that carry a NEW chip right now (What's New callouts
  // level 2 — set per-user from `new`-tagged entries, cleared on first use).
  highlights = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { flags } = useFeatureFlags();
  const activePath = location.pathname;
  const activeSearch = location.search;

  // RW-1786: two-tier rail behavior. Like calendarWriteBack this is a
  // behavior flag with NO admin bypass — read directly, defaults ON.
  // OFF restores the pre-Wave-1 flyout (open on demand, close on navigate).
  const rail2On = flags?.navRail2?.enabled !== false;

  // RW-1302 — FLOW streak badge. Reads the user's own public flowLeaderboard
  // summary (server-synced on every scorecard write) so a submit moves the
  // badge live, and merges it into the badges map the renderers consume.
  const [flowStreak, setFlowStreak] = useState(0);
  useEffect(() => {
    if (!user?.uid) return undefined;
    return subscribe(`flowLeaderboard/${user.uid}`, (docData) => {
      setFlowStreak(typeof docData?.streak === 'number' ? docData.streak : 0);
    }, () => {});
  }, [user?.uid]);
  const allBadges = useMemo(() => ({ ...badges, flowStreak }), [badges, flowStreak]);

  /* ── Role gate shared by the sections builder AND the footer filter ──
     Phase 5 (RW-1068): staffSelfOnly = the staff_self self-service role and NOT
     an admin (admins already get the full Staff · HR workspace at /staff). */
  const staffSelf = useMemo(() => isStaffSelf(user) && !isAdmin, [user, isAdmin]);
  // RW-1075b: One Pipeline gates in the rail — mirror DealsRolesContext
  // (canReview = complianceApi REVIEW_ROLES admin|tc|broker; canLead =
  // admin|broker) so the second rail and the surfaces can't disagree.
  const dealRole = user?.role || '';
  // RW-1787: staff variant = admin | tc | broker (DealsRolesContext's review
  // set — no new role system). Everyone else, including trainers, office
  // coordinators and staff_self users, stays on the agent tree, whose footer
  // keeps their role-gated rows.
  const staffVariant = isAdmin || dealRole === 'tc' || dealRole === 'broker';
  // RW-1568: the gate itself now lives in navigation.js so global search can
  // apply the identical test — search must never offer a page the rail would
  // have withheld, nor withhold one the rail shows.
  const roleAllows = useCallback(
    item => navRoleAllows(item, { isAdmin, isTrainer, isStaff, staffSelf, dealRole }),
    [isAdmin, isTrainer, isStaff, staffSelf, dealRole],
  );

  /* ── Filter sections + items by feature flags (admins bypass) ──
     RW-899 stage 1: when the account is winding down (offboarding started),
     filter the nav model itself to the Deals-module + account allowlist —
     one data-source filter instead of per-item conditionals. Workflow gate
     only; the App.js route gate backs it and stage 2 revokes for real. */
  // RW-2378: the computation itself moved to navigation.js so MobileNav runs
  // the IDENTICAL one. It used to be inline here, and the mobile bottom bar
  // grew its own hardcoded lists beside it — 8 of 12 sections never appeared
  // there. Behaviour is unchanged; only the home of the code moved.
  // RW-1786: bands are filtered per band so the banded structure survives for
  // the two-tier sub-rail; `items` stays the flat view every other consumer
  // (badges, active-section lookup, mobile drawer) already reads.
  const visibleSections = useMemo(() =>
    visibleNavSections(staffVariant ? STAFF_SECTIONS : SECTIONS, {
      roleAllows,
      isFlagEnabled: key => isFeatureEnabled(key, flags, isAdmin),
      // RW-2581 rail curation. RW-2624: admins bypass it like any module
      // flag — Admin view shows every section. `isAdmin` is already false
      // under "Viewing as Agent" (App.js), which is where an admin checks
      // the curated rail the Admin → Feature Flags › Rail toggles produce.
      isRailEnabled: key => isFeatureEnabled(key, flags, isAdmin),
      isPathAllowed: path => !windingDown || isWindDownAllowedPath(path),
    }),
    [flags, isAdmin, windingDown, roleAllows, staffVariant],
  );

  /* Inject conditional "My Onboarding" into the Academy section (RW-1785:
     it moved from the old My Work — the "Me" band — condition unchanged). */
  const sections = useMemo(() =>
    visibleSections.map(s => {
      if (s.id !== 'academy') return s;
      // RW-899: /onboarding is not reachable while winding down.
      // RW-1634 T1-13: completed agents keep a permanent door to the
      // /onboarding "You're fully live" recap — the checklist promises
      // "You can always come back to this from the sidebar".
      if (isAdmin || isTrainer || windingDown) return s;
      const label = onboardingProgress ? 'My Onboarding' : (onboardingComplete ? 'Onboarding recap' : null);
      if (!label) return s;
      const row = { key: 'MyOnboarding', label, path: '/onboarding', icon: 'clipboard' };
      // RW-1786: the row rides in its own "Me" band on the two-tier rail.
      return { ...s, items: [...s.items, row], bands: [...(s.bands || []), { label: 'Me', items: [row] }] };
    }),
    [visibleSections, onboardingProgress, onboardingComplete, isAdmin, isTrainer, windingDown],
  );

  /* ── Visible footer items → collapsed "More" section ── */
  const footerItems = useMemo(() => (staffVariant ? STAFF_FOOTER_ITEMS : FOOTER_ITEMS).filter(i => {
    // RW-899: winding-down accounts keep Settings only (same path allowlist).
    if (windingDown && !isWindDownAllowedPath(i.path)) return false;
    return roleAllows(i); // adminOnly / trainerOrAdmin / staffOnly / staffSelfOnly
  }), [roleAllows, windingDown, staffVariant]);

  /* Full ordered rail-section list: standalone + grouped + synthetic More. */
  const railSections = useMemo(() => {
    // RW-1600: rail order = declaration order (standalone sections can sit
    // anywhere in the sequence — FLOW/Academy ride after Deals).
    const list = [...sections];
    if (footerItems.length) {
      list.push({ id: 'more', icon: 'settings', label: 'More', subtitle: 'Settings & tools', items: footerItems });
    }
    return list;
  }, [sections, footerItems]);

  const activeSection = useMemo(
    () => sectionForPath(railSections, activePath),
    [railSections, activePath],
  );

  /* ── Sub-rail: which section is open (null = hidden on first load) ── */
  const [openId, setOpenId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  /* RW-1818: collapsed state of the standing sub-rail — full (labels+bands)
     vs the icon-only mini strip. A content click collapses; the » button or
     a spine re-click expands. Sticky across routes and reloads. */
  // RW-2526: stored per user rather than per browser. The value read at first
  // paint still comes from localStorage (useUserPreference mirrors it), so the
  // rail draws at its remembered width with no round trip — the server copy
  // only matters on a device that has not seen this user before.
  //
  // The `rail2On` guard the write used to carry is gone rather than translated:
  // every setMini call below is already inside a rail2On branch, so the guard
  // was describing a state that cannot occur.
  const [mini, setMini] = useUserPreference('navRailMini', false, { fromLegacy: legacyBoolean });
  /* Hover tooltip for mini icons: { label, top } in viewport coords. */
  const [miniTip, setMiniTip] = useState(null);

  /* Theme is a fixed user/org setting (default Cream), changed from Settings —
     not an in-app toggle. Re-read on the Settings write event + cross-tab. */
  const [themeKey, setThemeKey] = useState(resolveTheme);
  useEffect(() => {
    const sync = () => setThemeKey(resolveTheme());
    window.addEventListener('navrailthemechange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('navrailthemechange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const openSection = useMemo(
    () => (openId == null ? null : railSections.find(s => s.id === openId && !s.standalone) || null),
    [openId, railSections],
  );

  /* Broadcast effective rail width so App.js can offset header + main. */
  useEffect(() => {
    if (mobile) return;
    const width = SLIM_W + (openSection ? (rail2On && mini ? MINI_W : SUB_W) : 0);
    try { window.dispatchEvent(new CustomEvent(RESIZE_EVENT, { detail: { width } })); } catch {} // eslint-disable-line no-empty
  }, [openSection, mobile, rail2On, mini]);

  /* RW-1786: the last section the user actually opened, read only as the
     unmapped-route fallback below and written by the effect after it.

     RW-2526: openId itself is NOT the preference — it follows the route on
     every navigation, and a preference that changed on every click would be a
     write loop. The preference is what this pair has always stored: the last
     section opened. useUserPreference drops a write whose value has not
     changed, so routing around inside one section stays silent.

     Held in a ref as well because the fallback reads it inside a setState
     updater, where render state would be a stale frame. Declared above both
     effects rather than beside its writer, so neither reads it before it
     exists. */
  const [savedSection, saveSection] = useUserPreference('navRailSection', null);
  const savedSectionRef = useRef(savedSection);
  savedSectionRef.current = savedSection;

  /* RW-1786 (rail2 ON): the sub-rail is standing chrome — on every route
     change it FOLLOWS the route's section (deep links select the correct
     spine + sub-rail; route always wins over clicked/stored state). On an
     unmapped route the current panel stays; first load falls back to the
     persisted last section.
     Legacy (rail2 OFF) keeps RW-1195: the flyout must not linger over the
     module the user just entered. */
  useEffect(() => {
    setMobileOpen(false);
    if (!rail2On) { setOpenId(null); return; }
    const sec = sectionForPath(railSections, activePath);
    if (sec) { setOpenId(sec); return; }
    setOpenId(prev => {
      if (prev != null) return prev;
      const stored = savedSectionRef.current;
      return railSections.some(s => s.id === stored && !s.standalone) ? stored : prev;
    });
  }, [activePath, rail2On, railSections]);

  /* Persist it (navRail* namespace, beside navRailTheme). */
  useEffect(() => {
    if (!rail2On || openId == null || openId === 'more') return;
    saveSection(openId);
  }, [openId, rail2On, saveSection]);

  /* RW-1195: dismiss the sub-rail on outside mousedown or Escape. The rail
     contains-checks prevent a close-then-reopen race with the section
     toggle buttons. */
  const rail1Ref = useRef(null);
  const rail2Ref = useRef(null);
  useEffect(() => {
    if (!openSection || mobile) return;
    // RW-1818 (rail2 ON): a click on the page content collapses the full
    // sub-rail to the icon mini strip — no reaching for the ✕. Escape too.
    // Legacy (rail2 OFF) keeps the flyout dismissal: outside click closes.
    if (rail2On && mini) return; // already collapsed; content clicks are just clicks
    const collapse = () => { if (rail2On) setMini(true); else setOpenId(null); };
    const onDown = (e) => {
      if (rail1Ref.current?.contains(e.target) || rail2Ref.current?.contains(e.target)) return;
      collapse();
    };
    const onKey = (e) => { if (e.key === 'Escape') collapse(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openSection, mobile, rail2On, mini, setMini]);

  const theme = DUAL_THEMES[themeKey] || DUAL_THEMES.cream;

  /* ── role-home indicator (TC → My Tasks, Trainer → Training) ── */
  const roleHomeKey = useMemo(() => {
    const r = user?.role;
    if (r === 'tc') return 'MyTasks';
    if (r === 'trainer') return 'Training';
    return null;
  }, [user?.role]);

  /* ── Sub-rail badge count for a section (for the slim-rail dot/count) ──
     flowStreak is celebratory, not actionable — it stays off the section
     total so the slim-rail count keeps meaning "things needing you". */
  const sectionBadgeTotal = useCallback(section => {
    // RW-1600: standalone sections carry their own badge (FLOW streak — the
    // whole point of the promotion is the nudge being visible on the rail).
    if (!section.items) {
      const b = section.badgeKey ? (allBadges[section.badgeKey] || 0) : 0;
      return typeof b === 'number' ? b : 0;
    }
    return section.items.reduce((n, it) => {
      const b = it.badgeKey && it.badgeKey !== 'flowStreak' ? (allBadges[it.badgeKey] || 0) : 0;
      return n + (typeof b === 'number' ? b : 0);
    }, 0);
  }, [allBadges]);

  /* RW-1788: filter rows carry a query in their path (/feed?filter=important,
     the /messages?open= convention). A filter row is active when pathname AND
     query match; its base row (Feed) yields while the filter row is active. */
  const queryActivePaths = useMemo(() => {
    const set = new Set();
    for (const s of railSections) {
      if (s.standalone) continue;
      for (const it of s.items) {
        const qi = it.path.indexOf('?');
        if (qi === -1) continue;
        const base = it.path.slice(0, qi);
        if (pathActive(base, activePath) && activeSearch.includes(it.path.slice(qi + 1))) set.add(base);
      }
    }
    return set;
  }, [railSections, activePath, activeSearch]);

  const goSection = useCallback(s => {
    if (s.standalone) {
      track(EVENTS.NAV_SECTION_SELECTED, { sectionId: s.id, reselect: pathActive(s.path, activePath), standalone: true });
      markNavSource('rail');
      setOpenId(null); navigate(s.path); return;
    }
    track(EVENTS.NAV_SECTION_SELECTED, { sectionId: s.id, reselect: openId === s.id });
    if (rail2On) {
      if (openId !== s.id) {
        // RW-1786: selecting a section routes to its first visible item —
        // never a rail change without a page change. Opens full: labels help
        // while choosing; a content click re-collapses (RW-1818).
        setOpenId(s.id);
        setMini(false);
        if (s.items[0]) { markNavSource('rail'); navigate(s.items[0].path); }
      } else {
        // RW-1818: re-click toggles full ↔ mini instead of closing.
        setMini(m => !m);
      }
      return;
    }
    setOpenId(prev => (prev === s.id ? null : s.id));
  }, [navigate, rail2On, openId, activePath, setMini]);

  /* ═══════════════════════════ Sub-rail item ═══════════════════════════ */
  const renderItem = (item, bandLabel = null) => {
    const qi = item.path.indexOf('?');
    const itemPath = qi === -1 ? item.path : item.path.slice(0, qi);
    const itemQuery = qi === -1 ? null : item.path.slice(qi + 1);
    const active = !item.noActive && pathActive(itemPath, activePath)
      && (itemQuery ? activeSearch.includes(itemQuery) : !queryActivePaths.has(itemPath));
    const badge = item.badgeKey ? allBadges[item.badgeKey] : null;
    const isOnb = item.key === 'MyOnboarding';
    // RW-697: design shows a pending-task COUNT (not a percent — the % lives in
    // the top progress banner). Badge hides when the agent has no items left.
    // A red dot flags newly-assigned tasks the agent hasn't viewed yet.
    const onbCount = isOnb ? (onboardingProgress?.pending || 0) : 0;
    const onbHasNew = isOnb && onboardingNewCount > 0;
    return (
      <button
        key={item.key}
        type="button"
        role="menuitem"
        onClick={() => {
          // RW-1789: item selection + the one-shot rail marker for the
          // route-reached event App.js emits on the pathname change.
          track(EVENTS.NAV_ITEM_SELECTED, { sectionId: openId, band: bandLabel, itemKey: item.key, route: item.path });
          markNavSource('rail');
          navigate(item.path);
        }}
        aria-current={active ? 'page' : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          // RW-1788: filter rows (Important Posts) indent under their base row.
          width: '100%', minHeight: 38, padding: item.indent ? '9px 11px 9px 26px' : '9px 11px',
          border: 'none', borderRadius: 8, cursor: 'pointer',
          background: active ? theme.itemActiveBg : 'transparent',
          // RW-1786: active row = 3px accent left rule + hairline ring + lift.
          boxShadow: active
            ? (rail2On
              ? [`inset 3px 0 0 ${theme.accent}`, `0 0 0 1px ${theme.chipBorder}`, theme.itemActiveShadow].filter(sh => sh && sh !== 'none').join(', ')
              : theme.itemActiveShadow)
            : 'none',
          color: active ? theme.itemActiveText : theme.itemText,
          textAlign: 'left', fontFamily: SANS,
          fontSize: 13, fontWeight: active ? 600 : 500,
          transition: REDUCED_MOTION ? 'none' : 'background 120ms ease',
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = theme.activeBg; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        {item.icon && <NavIcon name={item.icon} size={15} />}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.label}
          {roleHomeKey === item.key && !active && (
            <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: theme.accent, marginLeft: 5, verticalAlign: 'middle' }} />
          )}
        </span>
        {(item.highlight || (highlights && highlights.has(item.key))) && !active && (
          <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: theme.tagText, background: theme.tagBg, padding: '2px 5px', borderRadius: 3 }}>NEW</span>
        )}
        {item.soon && (
          <span style={{ fontSize: 8, fontWeight: 700, color: theme.soft, border: `1px solid ${theme.chipBorder}`, padding: '2px 5px', borderRadius: 3 }}>SOON</span>
        )}
        {badge != null && badge > 0 && !item.soon && (
          <span style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: theme.badgeBg, color: theme.badgeText, fontSize: 10, fontWeight: 700, display: 'grid', placeItems: 'center' }}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        {isOnb && onbCount > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {onbHasNew && <span title="New tasks assigned" style={{ width: 6, height: 6, borderRadius: '50%', background: '#EF4444', display: 'inline-block', flexShrink: 0 }} />}
            <span style={{ minWidth: 18, height: 18, padding: '0 6px', borderRadius: 9, background: theme.badgeBg, color: theme.badgeText, fontSize: 10, fontWeight: 700, display: 'grid', placeItems: 'center', lineHeight: 1 }}>
              {onbCount > 99 ? '99+' : onbCount}
            </span>
          </span>
        )}
      </button>
    );
  };

  /* ═══════════════════════════ Avatar block ════════════════════════════ */
  const avatar = (size = 34) => (
    <div
      title={user?.displayName || ''}
      style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        border: `1px solid ${theme.avatarBorder}`,
        background: usablePhotoURL(user?.photoURL) ? `url(${usablePhotoURL(user.photoURL)}) center/cover` : theme.avatarFallbackBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.38, fontWeight: 700, color: theme.avatarFallbackText, fontFamily: SANS,
      }}
    >
      {!usablePhotoURL(user?.photoURL) ? (user?.displayName || '?').charAt(0).toUpperCase() : null}
    </div>
  );

  /* ── RW-1818: icon button for the collapsed mini strip ── */
  const renderMiniItem = (item, bandLabel = null) => {
    const qi = item.path.indexOf('?');
    const itemPath = qi === -1 ? item.path : item.path.slice(0, qi);
    const itemQuery = qi === -1 ? null : item.path.slice(qi + 1);
    const active = !item.noActive && pathActive(itemPath, activePath)
      && (itemQuery ? activeSearch.includes(itemQuery) : !queryActivePaths.has(itemPath));
    const badge = item.badgeKey ? allBadges[item.badgeKey] : null;
    return (
      <button
        key={item.key}
        type="button"
        role="menuitem"
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          track(EVENTS.NAV_ITEM_SELECTED, { sectionId: openId, band: bandLabel, itemKey: item.key, route: item.path });
          markNavSource('rail');
          navigate(item.path);
        }}
        onMouseEnter={(e) => {
          if (!active) e.currentTarget.style.background = theme.activeBg;
          const r = e.currentTarget.getBoundingClientRect();
          setMiniTip({ label: item.label, top: r.top + r.height / 2 - 13 });
        }}
        onMouseLeave={(e) => {
          if (!active) e.currentTarget.style.background = 'transparent';
        }}
        style={{
          position: 'relative', width: 32, height: 32, flexShrink: 0,
          display: 'grid', placeItems: 'center',
          border: 'none', borderRadius: 8, cursor: 'pointer',
          background: active ? theme.itemActiveBg : 'transparent',
          color: active ? theme.itemActiveText : theme.itemText,
          boxShadow: active ? `inset 3px 0 0 ${theme.accent}` : 'none',
          transition: REDUCED_MOTION ? 'none' : 'background 120ms ease',
        }}
      >
        <NavIcon name={item.icon} size={16} />
        {typeof badge === 'number' && badge > 0 && (
          <span style={{ position: 'absolute', top: -2, right: -4, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7, background: theme.badgeBg, color: theme.badgeText, fontSize: 8.5, fontWeight: 700, display: 'grid', placeItems: 'center', border: `1.5px solid ${theme.rail2Bg}` }}>{badge > 9 ? '9+' : badge}</span>
        )}
      </button>
    );
  };

  /* ═══════════════════════════ Desktop dual rail ═══════════════════════ */
  const desktopRail = (
    <>
      {/* ── Section rail (always visible) ── */}
      <nav
        ref={rail1Ref}
        aria-label="Main navigation"
        style={{
          ...railFrame(0, SLIM_W),
          background: theme.rail1Bg, borderRight: theme.rail1Border,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '14px 0 12px', gap: 6, flexShrink: 0, fontFamily: SANS,
        }}
      >
        {/* R wordmark tile */}
        <button
          type="button" aria-label="Home"
          onClick={() => { setOpenId(null); navigate('/dashboard'); }}
          style={{
            width: 32, height: 32, borderRadius: 8, background: theme.tileBg, border: 'none',
            display: 'grid', placeItems: 'center', color: theme.tileText, cursor: 'pointer',
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 18, fontWeight: 600, marginBottom: 6, flexShrink: 0,
          }}
        >R</button>

        {/* Section buttons.
            RW-2516: `minHeight: 0` is load-bearing. A flex item's min-height
            defaults to `auto` (= its content height), so without it this
            column could never shrink below the 12 staff sections and pushed
            the More / avatar footer out of the 100vh rail — "the footer is
            clipped on the left rail" on a MacBook viewport. The two sub-rail
            scrollers below already carried it; this one never did. */}
        <div data-testid="rail-sections" style={{ flex: 1, minHeight: 0, width: '100%', overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {railSections.map(s => {
            if (s.id === 'more') return null; // pinned below
            const on = !s.standalone && openId === s.id;
            const homeActive = s.standalone && pathActive(s.path, activePath);
            const dot = !s.standalone && activeSection === s.id && !on;
            const total = sectionBadgeTotal(s);
            return (
              <button
                key={s.id} type="button"
                onClick={() => goSection(s)}
                aria-current={homeActive ? 'page' : undefined}
                aria-expanded={s.standalone ? undefined : on}
                title={s.label}
                style={{
                  position: 'relative', width: 48, border: 'none', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  padding: '7px 0', borderRadius: 11,
                  background: (on || homeActive) ? theme.activeBg : 'transparent',
                  // RW-1786: 3px accent left rule on the active spine tile.
                  boxShadow: rail2On && (on || homeActive) ? `inset 3px 0 0 ${theme.accent}` : 'none',
                  transition: REDUCED_MOTION ? 'none' : 'background 120ms ease',
                }}
                onMouseEnter={(e) => { if (!on && !homeActive) e.currentTarget.style.background = theme.divider; }}
                onMouseLeave={(e) => { if (!on && !homeActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <NavIcon name={s.icon} size={19} color={(on || homeActive) ? theme.iconActive : theme.icon} />
                <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.02em', color: (on || homeActive) ? theme.labelActive : theme.label, maxWidth: 46, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                {/* RW-1608: standalone sections render their own badge — the
                    `dot` paths below require !standalone, which silently
                    dropped the RW-1600 FLOW streak. Coral, not alert-red:
                    the streak is celebratory, not "things needing you". */}
                {s.standalone && total > 0 ? (
                  <span style={{ position: 'absolute', top: 5, right: 6, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7, background: RP.coral, color: '#fff', fontSize: 8.5, fontWeight: 700, display: 'grid', placeItems: 'center', border: `1.5px solid ${theme.rail1Bg}` }}>{total > 9 ? '9+' : total}</span>
                ) : dot && total > 0 ? (
                  <span style={{ position: 'absolute', top: 5, right: 6, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7, background: '#EF4444', color: '#fff', fontSize: 8.5, fontWeight: 700, display: 'grid', placeItems: 'center', border: `1.5px solid ${theme.rail1Bg}` }}>{total > 9 ? '9+' : total}</span>
                ) : dot ? (
                  <span style={{ position: 'absolute', top: 7, right: 8, width: 6, height: 6, borderRadius: 3, background: theme.accent }} />
                ) : null}
              </button>
            );
          })}
        </div>

        {/* More (footer) + avatar pinned bottom.
            RW-2516: one footer block that can never be squeezed (`flexShrink:
            0`) — the scroll region above yields first. Same geometry as the
            three loose siblings it replaces: the nav's 6px gap now runs
            inside this column. */}
        <div data-testid="rail-footer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: '100%', flexShrink: 0 }}>
          {footerItems.length > 0 && (() => {
            const on = openId === 'more';
            const dot = activeSection === 'more' && !on;
            return (
              <button
                type="button"
                onClick={() => setOpenId(prev => (prev === 'more' ? null : 'more'))}
                aria-expanded={on}
                title="More"
                style={{
                  position: 'relative', width: 48, border: 'none', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  padding: '7px 0', borderRadius: 11, marginTop: 2,
                  background: on ? theme.activeBg : 'transparent',
                  transition: REDUCED_MOTION ? 'none' : 'background 120ms ease',
                }}
                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = theme.divider; }}
                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
              >
                <NavIcon name="settings" size={19} color={on ? theme.iconActive : theme.icon} />
                <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.02em', color: on ? theme.labelActive : theme.label }}>More</span>
                {dot && <span style={{ position: 'absolute', top: 7, right: 8, width: 6, height: 6, borderRadius: 3, background: theme.accent }} />}
              </button>
            );
          })()}
          <div style={{ width: 28, height: 1, background: theme.divider, margin: '6px 0 8px' }} />
          {avatar(34)}
        </div>
      </nav>

      {/* ── RW-1818: collapsed icon mini strip — click into the page and the
             sub-rail tucks into this; icons switch screens, hover shows the
             name in a bubble, » (or the spine icon) re-expands. ── */}
      {openSection && rail2On && mini && (
        <div
          ref={rail2Ref}
          data-testid="sub-rail-mini"
          style={{
            ...railFrame(SLIM_W, MINI_W),
            background: theme.rail2Bg, borderRight: `1px solid ${theme.rail2Border}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0', fontFamily: SANS,
          }}
          onMouseLeave={() => setMiniTip(null)}
        >
          <button
            type="button" onClick={() => setMini(false)} title="Expand" aria-label="Expand panel"
            style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', color: theme.closeColor, cursor: 'pointer', fontSize: 14, lineHeight: 1, marginBottom: 8, flexShrink: 0, display: 'grid', placeItems: 'center' }}
          >&raquo;</button>
          <div role="menu" style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, overflowY: 'auto', overflowX: 'hidden' }}>
            {(openSection.bands || [{ label: null, items: openSection.items }]).map((band, bi) => (
              <React.Fragment key={band.label || `band-${bi}`}>
                {bi > 0 && <div style={{ width: 22, height: 1, background: theme.divider, margin: '5px 0', flexShrink: 0 }} />}
                {band.items.map(item => renderMiniItem(item, band.label))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      {/* Hover bubble beside the mini strip */}
      {openSection && rail2On && mini && miniTip && (
        <div style={{ position: 'fixed', left: SLIM_W + MINI_W + 8, top: miniTip.top, zIndex: 75, background: RP.noir, color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: SANS, padding: '5px 9px', borderRadius: 6, whiteSpace: 'nowrap', pointerEvents: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
          {miniTip.label}
        </div>
      )}

      {/* ── Contextual sub-rail (full) ── */}
      {openSection && !(rail2On && mini) && (
        <div
          ref={rail2Ref}
          data-testid="sub-rail"
          style={{
            // RW-3797: content-box drew 210 + 24 padding + 1 border = 235px
            // here while the page reserved 210, covering its left 25px.
            ...railFrame(SLIM_W, SUB_W),
            background: theme.rail2Bg, borderRight: `1px solid ${theme.rail2Border}`,
            display: 'flex', flexDirection: 'column', padding: '18px 12px', fontFamily: SANS,
            boxShadow: REDUCED_MOTION ? 'none' : '6px 0 22px rgba(20,15,10,0.06)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '0 8px 12px' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: theme.subEyebrow, marginBottom: 3 }}>{openSection.label}</div>
              <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 500, color: theme.subTitle }}>{openSection.subtitle || openSection.label}</div>
            </div>
            <button
              type="button" onClick={() => { if (rail2On) setMini(true); else setOpenId(null); }} title="Collapse" aria-label="Collapse panel"
              style={{ width: 24, height: 24, borderRadius: 6, border: 'none', background: 'transparent', display: 'grid', placeItems: 'center', cursor: 'pointer', color: theme.closeColor, marginTop: 2, fontSize: 16, lineHeight: 1, flexShrink: 0 }}
            >&times;</button>
          </div>
          <div role="menu" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', overflowX: 'hidden' }}>
            {rail2On && openSection.bands ? (
              /* RW-1786: banded sub-rail — labels are presentational only
                 (uppercase, letterspaced), never clickable, never a tier. */
              openSection.bands.map((band, i) => (
                <div key={band.label || `band-${i}`} style={{ marginTop: i === 0 ? 0 : 12 }}>
                  {band.label && (
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.soft, padding: '0 11px 4px' }}>{band.label}</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{band.items.map(it => renderItem(it, band.label))}</div>
                </div>
              ))
            ) : (
              openSection.items.map(it => renderItem(it))
            )}
          </div>
        </div>
      )}
    </>
  );

  if (!mobile) return desktopRail;

  /* ═══════════════════════════ Mobile drawer ═══════════════════════════
     Secondary to the bottom MobileNav: a full grouped list in a slide-in
     drawer behind a hamburger. Always expanded (no accordion). */
  const mobileContent = (
    <aside
      aria-label="Main navigation"
      style={{ width: 280, maxWidth: '85vw', height: '100%', background: '#fff', display: 'flex', flexDirection: 'column', fontFamily: SANS }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 16px', borderBottom: `1px solid ${RP.line}`, minHeight: 56, flexShrink: 0 }}>
        <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 22, fontWeight: 600, color: RP.ink }}>Rêve</div>
        <button onClick={() => setMobileOpen(false)} aria-label="Close navigation" style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 22, lineHeight: 1, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
      </div>
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {railSections.map(s => {
          if (s.standalone) {
            const active = pathActive(s.path, activePath);
            return (
              <button key={s.id} type="button" onClick={() => navigate(s.path)} aria-current={active ? 'page' : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, padding: '10px 12px', border: 'none', borderRadius: 8, background: active ? RP.coralSoft : 'transparent', color: active ? RP.coralInk : RP.ink, fontFamily: SANS, fontSize: 15, fontWeight: 600, textAlign: 'left', cursor: 'pointer' }}>
                <NavIcon name={s.icon} size={18} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                {/* RW-1608: standalone badge (FLOW streak) — coral, celebratory */}
                {s.badgeKey && (allBadges[s.badgeKey] || 0) > 0 && (
                  <span style={{ background: RP.coral, color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, minWidth: 18, height: 18, padding: '0 5px', display: 'grid', placeItems: 'center' }}>{(allBadges[s.badgeKey] || 0) > 99 ? '99+' : allBadges[s.badgeKey]}</span>
                )}
              </button>
            );
          }
          return (
            <div key={s.id} style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: RP.mute2, padding: '4px 12px 4px' }}>{s.label}</div>
              {s.items.map(item => {
                const active = !item.noActive && pathActive(item.path, activePath);
                const badge = item.badgeKey ? allBadges[item.badgeKey] : null;
                return (
                  <button key={item.key} type="button" onClick={() => navigate(item.path)} aria-current={active ? 'page' : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 40, padding: '8px 12px 8px 16px', border: 'none', borderLeft: active ? `3px solid ${RP.coral}` : '3px solid transparent', borderRadius: '0 8px 8px 0', background: active ? RP.coralSoft : 'transparent', color: active ? RP.coralInk : RP.ink, fontFamily: SANS, fontSize: 15, fontWeight: active ? 600 : 400, textAlign: 'left', cursor: 'pointer' }}>
                    {item.icon && <NavIcon name={item.icon} size={15} />}
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                    {(item.highlight || (highlights && highlights.has(item.key))) && !active && (
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: RP.coralInk, background: RP.coralSoft, padding: '2px 5px', borderRadius: 3 }}>NEW</span>
                    )}
                    {badge != null && badge > 0 && (
                      <span style={{ background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, minWidth: 18, height: 18, padding: '0 5px', display: 'grid', placeItems: 'center' }}>{badge > 99 ? '99+' : badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>
      <div style={{ borderTop: `1px solid ${RP.line}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {avatar(32)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: RP.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.displayName || '—'}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.title || user?.role || ''}</div>
        </div>
      </div>
    </aside>
  );

  return (
    <>
      <button onClick={() => setMobileOpen(true)} aria-label="Open navigation"
        style={{ position: 'fixed', top: 'calc(12px + var(--reve-safe-top, 0px))', left: 12, zIndex: 55, width: 44, height: 44, borderRadius: 8, border: `1px solid ${RP.line}`, background: '#fff', color: '#374151', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>&#9776;</button>
      {mobileOpen && (
        <>
          <div onClick={() => setMobileOpen(false)} aria-hidden="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 59 }} />
          <div role="dialog" aria-modal="true" aria-label="Navigation menu" style={{ position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 61, boxShadow: '4px 0 24px rgba(0,0,0,0.12)' }}>
            <ModalOpenMarker />
            {mobileContent}
          </div>
        </>
      )}
    </>
  );
}

/* ─── Hook: consumed by App.js for main-content margin ───
   Slim rail is always 64px; widens to 64+210 while a sub-rail is open. */
export function useSidebarWidth() {
  const [width, setWidth] = useState(SLIM_W);
  useEffect(() => {
    const onResize = (e) => setWidth(e?.detail?.width ?? SLIM_W);
    window.addEventListener(RESIZE_EVENT, onResize);
    return () => window.removeEventListener(RESIZE_EVENT, onResize);
  }, []);
  return width;
}
